import { Options, Splide } from '@splidejs/splide'

import HttpService from '../Service/HttpService'
import { isTabletWideDown, mediaQueries } from '../Utility/Breakpoint'
import { getElementChild, getElements } from '../Utility/Dom'
import {
  AbstractTeaserList,
  BaseTeaserItem,
  HydraResponse,
  TeaserImageData,
} from './AbstractTeaserList'

const SLIDER_BUFFER_ZONE = 3

interface PublicationImageVariants {
  '392x261': null | string
  '392x261_2x': null | string
}

interface PublicationItem extends BaseTeaserItem {
  imageData?: TeaserImageData
  imageVariants?: PublicationImageVariants
  issueNumber?: null | string
  tags?: Tag[]
  type?: null | string
}

interface Tag {
  title: string
  uid: number
}

class PublicationList extends AbstractTeaserList<PublicationItem> {
  private hasMoreSliderItems = false

  private readonly layout: number
  private slider: null | Splide = null
  private readonly sliderElement: HTMLElement | null
  private sliderMounted = false

  constructor(container: HTMLElement) {
    const apiUrlBase = container.dataset.apiUrl ?? ''
    super(container, {
      apiUrl: apiUrlBase === '' ? '' : `${apiUrlBase.replace(/\/$/, '')}/publications`,
      containerSelector: 'publication-list',
      itemsPerPage: 8,
      listSelector: 'publication-list-items',
      templateSelector: 'publication-item-template',
    })

    this.layout = parseInt(container.dataset.layout ?? '0', 10)
    this.sliderElement = getElementChild(this.container, 'publication-slider')
  }

  public init(): void {
    if (this.layout === 0) {
      this.initSlider()
    } else if (this.layout === 1) {
      this.initResponsiveSlider()
    }
  }

  protected createItem(item: PublicationItem): HTMLElement | null {
    if (this.template === null) {
      return null
    }

    const fragment = this.template.content.cloneNode(true) as DocumentFragment
    const element = fragment.firstElementChild as HTMLElement

    if (element === null) {
      return null
    }

    const badge = getElementChild(element, 'publication-badge')
    const fallback = getElementChild(element, 'publication-fallback')
    const info = getElementChild(element, 'publication-info')
    const link = getElementChild(element, 'publication-link') as HTMLAnchorElement | null
    const picture = getElementChild(element, 'publication-picture')
    const tags = getElementChild(element, 'publication-tags')
    const title = getElementChild(element, 'publication-title')
    const type = getElementChild(element, 'publication-type')

    if (picture !== null && this.updatePictureSources(picture, item)) {
      fallback?.remove()
    }

    if (info !== null && badge !== null && item.category) {
      badge.textContent = item.category.title

      if (item.category.color) {
        badge.classList.add('bg-' + item.category.color)
      }
    } else {
      info?.remove()
    }

    if (link !== null) {
      link.href = item.detailLink || item.slug
    }

    if (title !== null) {
      title.textContent = item.teaserTitle || item.title
    }

    if (tags !== null && ((item.tags && item.tags.length > 0) || item.issueNumber)) {
      if (item.tags) {
        item.tags.forEach((tag: Tag): void => {
          const li = document.createElement('li')
          li.className = 'tag'
          li.textContent = tag.title
          tags.appendChild(li)
        })
      }

      if (item.issueNumber) {
        const li = document.createElement('li')
        li.className = 'tag'
        li.textContent = item.issueNumber
        tags.appendChild(li)
      }
    } else {
      tags?.remove()
    }

    if (type !== null && item.type) {
      type.textContent = item.type
    } else {
      type?.remove()
    }

    if (this.layout === 0 || (this.layout === 1 && this.sliderMounted)) {
      element.classList.add('splide__slide')
    }

    return element
  }

  protected updatePictureSources(picture: HTMLElement, item: PublicationItem): boolean {
    const variants = item.imageVariants
    const img = picture.querySelector('img')

    if (!variants || !variants['392x261'] || img === null) {
      picture.remove()

      return false
    }

    const src = variants['392x261']
    const srcset = `${src} 1x${variants['392x261_2x'] ? `, ${variants['392x261_2x']} 2x` : ''}`

    picture.querySelectorAll('source').forEach((source: HTMLSourceElement): void => {
      source.srcset = srcset
    })

    img.src = src
    img.srcset = srcset
    img.alt = item.imageData?.alternative || item.teaserTitle || item.title

    return true
  }

  private addSlideClasses(): void {
    if (this.list === null) {
      return
    }

    const items = this.list.children

    for (let i = 0; i < items.length; i++) {
      items[i].classList.add('splide__slide')
    }
  }

  private addSliderLazyLoad(): void {
    if (this.slider === null) {
      return
    }

    this.slider.on('moved', (newIndex: number): void => {
      if (this.slider === null || this.isLoading === true || this.hasMoreSliderItems === false) {
        return
      }

      const totalSlides = this.slider.Components.Elements.list.childElementCount
      const perPage = this.slider.options.perPage ?? 4
      const remainingSlides = totalSlides - newIndex - perPage

      if (remainingSlides <= SLIDER_BUFFER_ZONE) {
        this.loadSliderItems()
      }
    })

    this.slider.on('arrows:updated', (_prev: HTMLButtonElement, next: HTMLButtonElement): void => {
      if (this.hasMoreSliderItems === true && next.disabled === true) {
        requestAnimationFrame((): void => {
          next.disabled = false
        })
      }
    })
  }

  private addSliderSkeletonSlides(): void {
    if (this.slider === null || this.skeleton === null) {
      return
    }

    const skeletonItem = this.skeleton.querySelector('.skeleton__item')

    if (skeletonItem === null) {
      return
    }

    for (let i = 0; i < this.itemsPerPage; i++) {
      const clone = skeletonItem.cloneNode(true) as HTMLElement
      clone.dataset.skeletonSlide = 'true'
      this.slider?.add(clone)
    }
  }

  private buildSliderApiUrl(): string {
    const params = new URLSearchParams()

    if (this.isRecordMode) {
      this.recordSlice(this.currentPage).forEach((uid: number): void => {
        params.append('uid[]', uid.toString())
      })

      return `${this.apiUrl}?${params.toString()}`
    }

    params.set('page', this.currentPage.toString())
    params.set('itemsPerPage', this.itemsPerPage.toString())

    if (this.tagId !== null) {
      params.set('tags', this.tagId.toString())

      return `${this.apiUrl}?${params.toString()}`
    }

    this.pageIds.forEach((pid: number): void => {
      params.append('pid[]', pid.toString())
    })

    return `${this.apiUrl}?${params.toString()}`
  }

  private getSliderOptions(): Options {
    return {
      arrows: true,
      /* eslint-disable perfectionist/sort-objects -- splide requires descending breakpoint order */
      breakpoints: {
        1024: {
          perMove: 1,
          perPage: 2,
        },
        768: {
          pagination: false,
          perMove: 1,
          perPage: 2,
        },
        575: {
          focus: 'center',
          gap: '1rem',
          pagination: false,
          perMove: 1,
          perPage: 1.1,
        },
      },
      /* eslint-enable perfectionist/sort-objects */
      gap: '1.25rem',
      perMove: 1,
      perPage: 4,
      type: 'slide',
    }
  }

  private initResponsiveSlider(): void {
    if (this.sliderElement === null) {
      return
    }

    const breakpointQuery = mediaQueries.tabletWide.down
    const sliderOptions = this.getSliderOptions()

    this.slider = new Splide(this.sliderElement, sliderOptions)

    const toggleSlider = (isActive: boolean): void => {
      if (this.slider === null) {
        return
      }

      if (isActive) {
        if (!this.sliderMounted) {
          this.addSlideClasses()
          this.slider.mount()
          this.sliderMounted = true
          this.hasMoreSliderItems = this.isRecordMode
            ? this.recordIds.length > this.slider.length
            : this.slider.length >= this.itemsPerPage
          this.addSliderLazyLoad()
          this.slider.on('resized', (): void => this.updateControlsVisibility())
          this.updateControlsVisibility()
        }
      } else {
        if (this.sliderMounted) {
          this.slider.destroy(true)
          this.sliderMounted = false
          this.removeSlideClasses()
        }
      }
    }

    toggleSlider(isTabletWideDown())

    breakpointQuery.addEventListener('change', (event): void => {
      toggleSlider(event.matches)
    })
  }

  private initSlider(): void {
    if (this.sliderElement === null) {
      return
    }

    const sliderOptions = this.getSliderOptions()
    this.slider = new Splide(this.sliderElement, sliderOptions)
    this.slider.on('resized', (): void => this.updateControlsVisibility())
    this.slider.mount()
    this.sliderMounted = true
    this.hasMoreSliderItems = this.isRecordMode
      ? this.recordIds.length > this.slider.length
      : this.slider.length >= this.itemsPerPage
    this.addSliderLazyLoad()
    this.updateControlsVisibility()
  }

  private async loadSliderItems(): Promise<void> {
    if (this.slider === null || this.isLoading === true || this.apiUrl === '') {
      return
    }

    this.isLoading = true
    this.currentPage++
    this.addSliderSkeletonSlides()

    const url = this.buildSliderApiUrl()

    try {
      const response = await HttpService.get(url) as HydraResponse<PublicationItem>

      this.removeSliderSkeletonSlides()

      const items = this.isRecordMode
        ? this.orderBySelection(response['hydra:member'])
        : response['hydra:member']

      items.forEach((item: PublicationItem): void => {
        const element = this.createItem(item)

        if (element !== null) {
          this.slider?.add(element)
        }
      })

      this.hasMoreSliderItems = this.isRecordMode
        ? this.currentPage * this.itemsPerPage < this.recordIds.length
        : response['hydra:view']?.['hydra:next'] !== undefined
    } catch (error) {
      console.error('Error loading slider items:', error)
      this.removeSliderSkeletonSlides()
    } finally {
      this.isLoading = false
    }
  }

  private removeSlideClasses(): void {
    if (this.list === null) {
      return
    }

    const items = this.list.children

    for (let i = 0; i < items.length; i++) {
      items[i].classList.remove('splide__slide')
    }
  }

  private removeSliderSkeletonSlides(): void {
    if (this.slider === null) {
      return
    }

    const list = this.slider.Components.Elements.list
    const skeletonSlides = list.querySelectorAll('[data-skeleton-slide="true"]')

    skeletonSlides.forEach((el: Element): void => {
      const slide = el.closest('.splide__slide') ?? el
      slide.remove()
    })

    this.slider.refresh()
  }

  private updateControlsVisibility(): void {
    if (this.slider === null || this.sliderElement === null) {
      return
    }

    const controls = this.sliderElement.querySelector<HTMLElement>('.splide__controls')

    if (controls === null) {
      return
    }

    const perPage = Math.ceil(this.slider.options.perPage ?? 4)
    controls.style.display = this.slider.length <= perPage ? 'none' : ''
  }
}

const containers = getElements('publication-list')

containers.forEach((container: HTMLElement): void => {
  const publicationList = new PublicationList(container)
  publicationList.init()
})
